import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { listReceipts, deleteReceipt, pdfUrl } from "@/lib/api";
import { formatMoney, formatDate, imgSrc, effectiveValue, sortReceipts, sumByCurrency } from "@/lib/format";
import { SortHeader, SortMenu } from "@/components/SortControls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Download,
  Pencil,
  Trash2,
  Search,
  Plus,
  Hash,
  Wallet,
  Percent,
  Coins,
  LogOut,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
} from "lucide-react";
import { toast } from "sonner";

const SORT_OPTIONS = [
  { key: "date", label: "Datum" },
  { key: "supplier", label: "Lieferant" },
  { key: "purpose", label: "Zweck" },
  { key: "price", label: "Brutto" },
];

function StatCard({ icon: Icon, label, value, values, testId }) {
  const list = values ?? [value];
  const multi = list.length > 1;
  return (
    <div
      className="bg-primary border-[5px] border-white p-5 flex items-center gap-4 shadow-sm"
      data-testid={testId}
    >
      <div className="shrink-0">
        <Icon className="h-[30px] w-[30px] text-white" />
      </div>
      <div className="min-w-0">
        <div className="data-label !text-white">{label}</div>
        {multi ? (
          <div className="mt-1 space-y-0.5">
            {list.map((s) => (
              <div
                key={s}
                className="font-heading text-lg font-semibold tabular text-white leading-tight"
              >
                {s}
              </div>
            ))}
          </div>
        ) : (
          <div className="font-heading text-2xl font-semibold tabular mt-0.5 text-white">
            {list[0]}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleMobileLogout = async () => {
    await logout();
    navigate("/login");
  };
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc"); // default: newest date first
  const [openFolders, setOpenFolders] = useState(() => new Set());

  const toggleFolder = (key) =>
    setOpenFolders((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReceipts(await listReceipts());
    } catch {
      toast.error("Belege konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    return {
      count: receipts.length,
      totals: sumByCurrency(receipts),
    };
  }, [receipts]);

  const filtered = useMemo(() => {
    const f = receipts.filter((r) => {
      const purposeText = effectiveValue(r.purpose, r.purpose_other);
      return (
        !search ||
        r.supplier?.toLowerCase().includes(search.toLowerCase()) ||
        purposeText.toLowerCase().includes(search.toLowerCase())
      );
    });
    return sortReceipts(f, sortKey, sortDir);
  }, [receipts, search, sortKey, sortDir]);

  // Group receipts into collapsible folders (only used when NOT searching):
  // current month flat at top; past months of this year as month folders;
  // past years as year folders containing month folders.
  const grouped = useMemo(() => {
    const now = new Date();
    const curY = now.getFullYear();
    const curM = now.getMonth();
    const sorted = sortReceipts(receipts, sortKey, sortDir);
    const current = [];
    const undated = [];
    const mThis = {};
    const yMap = {};
    for (const r of sorted) {
      const ds = (r.date || "").slice(0, 10);
      const d = ds ? new Date(`${ds}T00:00:00`) : null;
      if (!d || Number.isNaN(d.getTime())) {
        undated.push(r);
        continue;
      }
      const y = d.getFullYear();
      const m = d.getMonth();
      if (y > curY || (y === curY && m >= curM)) current.push(r);
      else if (y === curY) (mThis[m] = mThis[m] || []).push(r);
      else {
        yMap[y] = yMap[y] || {};
        (yMap[y][m] = yMap[y][m] || []).push(r);
      }
    }
    const pastMonths = Object.keys(mThis)
      .map(Number)
      .sort((a, b) => b - a)
      .map((m) => ({ m, list: mThis[m] }));
    const years = Object.keys(yMap)
      .map(Number)
      .sort((a, b) => b - a)
      .map((y) => ({
        y,
        months: Object.keys(yMap[y])
          .map(Number)
          .sort((a, b) => b - a)
          .map((m) => ({ m, list: yMap[y][m] })),
      }));
    return { current, pastMonths, years, undated, curY, curM };
  }, [receipts, sortKey, sortDir]);

  const monthLabel = (y, m) =>
    new Date(y, m, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const monthName = (m) =>
    new Date(2000, m, 1).toLocaleDateString("de-DE", { month: "long" });
  const countLabel = (n) => `${n} ${n === 1 ? "Beleg" : "Belege"}`;
  const folderTotal = (list) =>
    sumByCurrency(list)
      .map((t) => formatMoney(t.grandTotal, t.currency))
      .join(" · ");

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteReceipt(toDelete.id);
      toast.success("Beleg gelöscht.");
      setToDelete(null);
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const downloadPdf = (r) => {
    const a = document.createElement("a");
    a.href = pdfUrl(r.id);
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const renderRow = (r) => (
    <div
      key={r.id}
      data-testid="receipt-row-item"
      className="grid grid-cols-2 md:grid-cols-[64px_1.6fr_1fr_1.2fr_1fr_120px] gap-3 md:gap-4 px-4 py-3 items-center hover:bg-muted/50 transition-colors duration-150 cursor-pointer"
      onClick={() => navigate(`/beleg/${r.id}`)}
    >
      <div className="row-start-1 md:row-auto">
        <div className="h-12 w-12 border border-border rounded-sm overflow-hidden bg-muted">
          {r.thumb_base64 || r.image_base64 ? (
            <img
              src={imgSrc(r.thumb_base64 || r.image_base64, r.image_mime)}
              alt={r.supplier}
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
      </div>
      <div className="font-medium truncate">
        {r.supplier || "—"}
        <div className="md:hidden text-xs text-muted-foreground">{formatDate(r.date)}</div>
      </div>
      <div className="hidden md:block text-sm tabular text-muted-foreground">
        {formatDate(r.date)}
      </div>
      <div className="hidden md:block text-sm truncate text-muted-foreground">
        {effectiveValue(r.purpose, r.purpose_other) || "—"}
      </div>
      <div className="text-right md:text-left font-semibold tabular">
        {formatMoney(r.price, r.currency)}
      </div>
      <div
        className="flex items-center justify-end gap-1 col-span-2 md:col-span-1"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="ghost"
          size="icon"
          title="PDF herunterladen"
          onClick={() => downloadPdf(r)}
          data-testid={`download-pdf-${r.id}`}
        >
          <Download className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          title="Bearbeiten"
          onClick={() => navigate(`/beleg/${r.id}/bearbeiten`)}
          data-testid={`edit-receipt-${r.id}`}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          title="Löschen"
          onClick={() => setToDelete(r)}
          data-testid={`delete-receipt-${r.id}`}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const folderHeader = (key, label, list, indent = 0) => {
    const open = openFolders.has(key);
    const Chevron = open ? ChevronDown : ChevronRight;
    const FolderIcon = open ? FolderOpen : Folder;
    return (
      <button
        type="button"
        onClick={() => toggleFolder(key)}
        aria-expanded={open}
        data-testid={`folder-${key}`}
        className={`w-full flex items-center gap-2 px-4 py-3 bg-muted/40 hover:bg-muted transition-colors text-left ${
          indent ? "md:pl-12" : ""
        }`}
      >
        <Chevron className="h-4 w-4 text-muted-foreground shrink-0" />
        <FolderIcon className="h-4 w-4 text-primary shrink-0" />
        <span className="font-medium">{label}</span>
        <span className="text-sm text-muted-foreground truncate">
          · {countLabel(list.length)} · {folderTotal(list)}
        </span>
      </button>
    );
  };

  return (
    <div data-testid="dashboard">
      {/* Mobile: capture-only landing */}
      <div
        className="md:hidden flex flex-col items-center justify-center text-center gap-10 py-12"
        data-testid="mobile-capture"
      >
        <Button
          onClick={() => navigate("/neu")}
          disabled={user?.topup_blocked}
          style={{ height: "150px" }}
          className="w-full max-w-sm text-xl gap-3"
          data-testid="mobile-new-receipt-button"
        >
          <Plus className="h-7 w-7" /> Neuer Beleg
        </Button>
        {user?.topup_blocked && (
          <p className="text-destructive text-sm max-w-xs -mt-6" data-testid="mobile-blocked-hint">
            Guthaben aufgebraucht – neue Belege können erst nach einer Aufladung erfasst werden.
          </p>
        )}
        <p className="text-muted-foreground text-sm max-w-xs">
          Die mobile Version von BeSca dient ausschließlich der Erfassung von
          Belegen.
        </p>
        <Button
          variant="secondary"
          onClick={handleMobileLogout}
          className="border border-border gap-2"
          data-testid="mobile-logout-button"
        >
          <LogOut className="h-4 w-4" /> Abmelden
        </Button>
      </div>

      {/* Desktop: full dashboard */}
      <div className="hidden md:block space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-medium">
            Belege
          </h1>
          <p className="text-muted-foreground mt-1">
            Erfasste Zahlungsbelege, ausgelesen und angereichert.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Hash} label="Anzahl Belege" value={stats.count} testId="stat-count" />
        <StatCard
          icon={Wallet}
          label="Gesamtbetrag"
          values={stats.totals.map((t) => formatMoney(t.grandTotal, t.currency))}
          testId="stat-total"
        />
        <StatCard
          icon={Coins}
          label="Davon Trinkgeld"
          values={stats.totals.map((t) => formatMoney(t.tip, t.currency))}
          testId="stat-tip"
        />
        <StatCard
          icon={Percent}
          label="Enthaltene MwSt."
          values={stats.totals.map((t) => formatMoney(t.vat, t.currency))}
          testId="stat-vat"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Nach Lieferant oder Zweck suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white"
            data-testid="search-input"
          />
        </div>
        <SortMenu
          options={SORT_OPTIONS}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortKey={(k) => setSortKey(k)}
          onToggleDir={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
          className="shrink-0"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="border border-border divide-y divide-border">
          {["sk-1", "sk-2", "sk-3", "sk-4"].map((k) => (
            <div key={k} className="h-20 bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : receipts.length === 0 ? (
        <div
          className="border border-dashed border-border bg-card flex flex-col items-center justify-center text-center py-16 px-6"
          data-testid="empty-state"
        >
          <h3 className="font-heading text-xl font-medium">Keine Belege vorhanden</h3>
          <p className="text-muted-foreground mt-1 max-w-sm">
            Laden Sie Ihren ersten Beleg über den Menüpunkt Neuer Beleg hoch. Wir lesen
            Lieferant, Datum, Preis und MwSt. automatisch aus.
          </p>
        </div>
      ) : search && filtered.length === 0 ? (
        <div
          className="border border-dashed border-border bg-card flex flex-col items-center justify-center text-center py-16 px-6"
          data-testid="empty-state"
        >
          <h3 className="font-heading text-xl font-medium">Keine Treffer</h3>
          <p className="text-muted-foreground mt-1 max-w-sm">Passen Sie die Suche an.</p>
        </div>
      ) : (
        <div
          className="border border-border bg-white overflow-hidden"
          data-testid="receipt-list"
        >
          <div className="hidden md:grid grid-cols-[64px_1.6fr_1fr_1.2fr_1fr_120px] gap-4 px-4 py-3 bg-muted border-b border-border">
            <div className="data-label" />
            <SortHeader label="Lieferant" colKey="supplier" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
            <SortHeader label="Datum" colKey="date" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
            <SortHeader label="Zweck" colKey="purpose" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
            <SortHeader label="Brutto" colKey="price" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
            <div className="data-label" />
          </div>

          {search ? (
            <div className="divide-y divide-border" data-testid="receipt-flat-list">
              {filtered.map((r) => renderRow(r))}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {grouped.current.length > 0 && (
                <div
                  className="px-4 py-2 bg-primary/5 text-sm font-medium text-primary"
                  data-testid="current-month-label"
                >
                  {monthLabel(grouped.curY, grouped.curM)} · {countLabel(grouped.current.length)} ·{" "}
                  {folderTotal(grouped.current)}
                </div>
              )}
              {grouped.current.map((r) => renderRow(r))}

              {grouped.pastMonths.map(({ m, list }) => {
                const key = `m-${grouped.curY}-${m}`;
                return (
                  <div key={key}>
                    {folderHeader(key, monthLabel(grouped.curY, m), list)}
                    {openFolders.has(key) && (
                      <div className="divide-y divide-border border-t border-border">
                        {list.map((r) => renderRow(r))}
                      </div>
                    )}
                  </div>
                );
              })}

              {grouped.years.map(({ y, months }) => {
                const yk = `y-${y}`;
                const yearList = months.flatMap((mm) => mm.list);
                return (
                  <div key={yk}>
                    {folderHeader(yk, String(y), yearList)}
                    {openFolders.has(yk) && (
                      <div className="divide-y divide-border border-t border-border">
                        {months.map(({ m, list }) => {
                          const mk = `m-${y}-${m}`;
                          return (
                            <div key={mk}>
                              {folderHeader(mk, monthName(m), list, 1)}
                              {openFolders.has(mk) && (
                                <div className="divide-y divide-border border-t border-border">
                                  {list.map((r) => renderRow(r))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {grouped.undated.length > 0 && (
                <div key="undated">
                  {folderHeader("undated", "Ohne Datum", grouped.undated)}
                  {openFolders.has("undated") && (
                    <div className="divide-y divide-border border-t border-border">
                      {grouped.undated.map((r) => renderRow(r))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Beleg löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Beleg von „{toDelete?.supplier || "Unbekannt"}" wird dauerhaft
              entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
}
